import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBook,
  faCog,
  faFileAlt,
  faTags,
  faList,
  faLayerGroup,
  faTrophy,
  faWrench,
  faShoppingBag,
  faTasks,
  faUsers,
  faMobileAlt,
  faTimes,
  faCompass,
  faChartPie,
  faMusic,
  faPlus,
  faChevronDown,
  faChevronRight,
  faCertificate,
  faCreditCard,
  faSyncAlt,
  faStream,
  faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';
import { canViewPaymentAdmin } from '../../utils/authConfig';
import logo from '../../assets/icons/logo.png';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const isExploreActive = location.pathname.startsWith('/explore');
  const [exploreExpanded, setExploreExpanded] = useState(isExploreActive);

  const exploreItems = [
    { path: '/explore/overview', icon: faChartPie, label: 'Overview' },
    { path: '/explore/public-songs', icon: faMusic, label: 'Public Songs' },
    { path: '/explore/playlists', icon: faList, label: 'Playlists' },
    { path: '/explore/new-releases', icon: faPlus, label: 'New Releases' },
    { path: '/explore/top-charts', icon: faTrophy, label: 'Top Charts' },
    { path: '/explore/moods-genres', icon: faTags, label: 'Moods & Genres' },
    { path: '/explore/admin-playlists', icon: faLayerGroup, label: 'Admin Playlist' }
  ];

  useEffect(() => {
    if (location.pathname.startsWith('/explore')) {
      setExploreExpanded(true);
    }
  }, [location.pathname]);

  const navItems = [
    { path: '/dashboard', icon: faHome, label: 'Dashboard' },
    { path: '/logs', icon: faFileAlt, label: 'Error Logs' },
    { path: '/sync-request-logs', icon: faSyncAlt, label: 'Sync Request Logs' },
    { path: '/mobile-activity-logs', icon: faStream, label: 'Mobile Activity Logs' },
    { path: '/song-models', icon: faBook, label: 'Song Models' },
    { path: '/categories', icon: faTags, label: 'Categories' },
    { path: '/products', icon: faShoppingBag, label: 'Products' },
    { path: '/tasks', icon: faTasks, label: 'Task Manager' },
    { path: '/song-certificates', icon: faCertificate, label: 'Song Certificates' },
    { path: '/sync-users', icon: faUsers, label: 'Sync Users' },
    { path: '/apple-transfer', icon: faExchangeAlt, label: 'Apple Transfer' },
    { path: '/devices', icon: faMobileAlt, label: 'Devices' },
    ...(canViewPaymentAdmin() ? [{ path: '/payments', icon: faCreditCard, label: 'Payments' }] : []),
  ];
  const settingsItems = [
    { path: '/settings/genres', icon: faList, label: 'Genres' },
    { path: '/settings/moods', icon: faLayerGroup, label: 'Moods' },
    { path: '/settings/config', icon: faWrench, label: 'Config' },
    { path: '/settings/app-versions', icon: faMobileAlt, label: 'App Versions' }
  ];

  const handleLinkClick = () => {
    // Close sidebar on mobile when a link is clicked
    if (window.innerWidth < 992 && onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}
      
      <aside className={`menu ${isOpen ? 'show' : ''}`}>
        <div className="menu-inner">
          <div className="menu-header d-lg-none">
            <h1 className="navbar-brand navbar-brand-autodark mb-0">
              <Link to="/dashboard" className="d-flex align-items-center ps-2" onClick={handleLinkClick}>
                <img src={logo} alt="SongSnap Dashboard Logo" style={{ height: '32px', marginRight: '10px'}} />
                SS Dashboard
              </Link>
            </h1>
            <button
              className="btn btn-link text-secondary p-0"
              type="button"
              onClick={onClose}
              aria-label="Close menu"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>
          </div>
          
          <h1 className="navbar-brand navbar-brand-autodark d-none d-lg-block">
            <Link to="/dashboard" className="d-flex align-items-center ps-2">
              <img src={logo} alt="SongSnap Dashboard Logo" style={{ height: '32px', marginRight: '10px'}} />
              SS Dashboard
            </Link>
          </h1>
          
          <nav className="menu-nav" id="navbar-menu" aria-label="Main">
            <ul className="menu-nav-list pt-lg-3">
              {navItems.map((item) => (
                <li className="nav-item" key={item.path}>
                  <Link
                    to={item.path}
                    className={`nav-link ${
                      location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path + '/')) ? 'active' : ''
                    }`}
                    onClick={handleLinkClick}
                  >
                    <FontAwesomeIcon icon={item.icon} className="nav-link-icon" />
                    <span className="nav-link-title">{item.label}</span>
                  </Link>
                </li>
              ))}
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link border-0 bg-transparent w-100 text-start ${isExploreActive ? 'active' : ''}`}
                  onClick={() => setExploreExpanded(!exploreExpanded)}
                >
                  <FontAwesomeIcon icon={faCompass} className="nav-link-icon" />
                  <span className="nav-link-title">Explore</span>
                  <FontAwesomeIcon
                    icon={exploreExpanded ? faChevronDown : faChevronRight}
                    className="ms-auto"
                    style={{ fontSize: '0.7rem' }}
                  />
                </button>
                {exploreExpanded && (
                  <ul className="menu-nav-list menu-nav-list-nested">
                    {exploreItems.map((item) => (
                      <li className="nav-item" key={item.path}>
                        <Link
                          to={item.path}
                          className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                          onClick={handleLinkClick}
                          style={{ fontSize: '0.9rem' }}
                        >
                          <FontAwesomeIcon icon={item.icon} className="nav-link-icon me-2" />
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              <li className="nav-item mt-3">
                <span className="nav-link disabled" style={{ opacity: 0.7 }}>
                  <FontAwesomeIcon icon={faCog} className="nav-link-icon" />
                  <span className="nav-link-title">Settings</span>
                </span>
              </li>
              {settingsItems.map((item) => (
                <li className="nav-item" key={item.path}>
                  <Link
                    to={item.path}
                    className={`nav-link ${
                      location.pathname === item.path ? 'active' : ''
                    }`}
                    onClick={handleLinkClick}
                  >
                    <FontAwesomeIcon icon={item.icon} className="nav-link-icon" />
                    <span className="nav-link-title">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar; 